import { useStores, type File } from '@littlebird/sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  artifactPolicy,
  getFileVersionKey,
  type ArtifactSurface,
} from './artifact-presentation';
import {
  artifactBodyKey,
  artifactBodyQueryOptions,
  artifactHeadKey,
  artifactUrlKey,
  enforceArtifactCacheBudget,
  fetchArtifactHead,
  resolveArtifactUrl,
} from './artifact.query';

export type ArtifactContentStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'too_large';

const CANVAS_TITLE_PROBE_BYTES = 2047;

interface UseArtifactContentOptions {
  surface: ArtifactSurface;
  enabled: boolean;
  canvasTitleProbe?: boolean;
}

export function useArtifactContent(
  file: File | undefined,
  { surface, enabled, canvasTitleProbe = false }: UseArtifactContentOptions
) {
  const { files: filesStore } = useStores();
  const queryClient = useQueryClient();
  const policy = artifactPolicy(file);
  const mode = policy[surface];
  const renderer = policy.renderer;
  const versionKey = file ? getFileVersionKey(file) : 'no-file';
  const isBodyMode = mode === 'text' || mode === 'binary';
  const knownTooLarge = isBodyMode && !!file && file.size > policy.byteCap;

  const resolveUrl = () =>
    file ? filesStore.getFileUrl(file) : Promise.resolve(undefined);

  const wantsBody = enabled && isBodyMode && !knownTooLarge;

  const headQuery = useQuery({
    queryKey: artifactHeadKey(versionKey),
    enabled:
      canvasTitleProbe && renderer === 'canvas' && !wantsBody && !knownTooLarge,
    staleTime: Infinity,
    retry: false,
    queryFn: async ({ signal }) => {
      const { partial, body } = await fetchArtifactHead(
        resolveUrl,
        CANVAS_TITLE_PROBE_BYTES,
        policy.byteCap,
        signal
      );
      if (body) {
        queryClient.setQueryData(artifactBodyKey(versionKey), body);
        enforceArtifactCacheBudget(queryClient);
      }
      return partial;
    },
  });

  const bodyQuery = useQuery({
    ...artifactBodyQueryOptions(
      queryClient,
      versionKey,
      resolveUrl,
      renderer,
      policy.byteCap
    ),
    enabled: wantsBody && !headQuery.isFetching,
  });

  const urlQuery = useQuery({
    queryKey: artifactUrlKey(versionKey),
    enabled: enabled && mode === 'url',
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: () => resolveArtifactUrl(resolveUrl),
  });
  const freshUrl = urlQuery.data;

  const body = bodyQuery.data;
  const isFailed =
    (wantsBody && bodyQuery.isError) || (mode === 'url' && urlQuery.isError);

  const deriveStatus = (): ArtifactContentStatus => {
    if (knownTooLarge) return 'too_large';
    if (mode === 'none') return 'idle';
    if (isFailed) return 'error';
    if (mode === 'url') {
      if (freshUrl) return 'ready';
      return enabled ? 'loading' : 'idle';
    }
    if (body) return body.outcome === 'too_large' ? 'too_large' : 'ready';
    return wantsBody ? 'loading' : 'idle';
  };
  const status = deriveStatus();

  const canvas = body?.outcome === 'ready' ? body.canvas : undefined;
  const headTitle = headQuery.data?.title;

  return {
    renderer,
    status,
    text: body?.outcome === 'ready' ? body.text : '',
    bytes: body?.outcome === 'ready_bytes' ? body.bytes : undefined,
    url: mode === 'url' ? (freshUrl ?? '') : '',
    canvas,
    title: canvas?.success ? canvas.canvasPackage.title : headTitle,
    versionKey,
  };
}
